/**
 * Controls Component - Recording control bar
 *
 * Features:
 * - Mute/unmute toggle
 * - Camera on/off toggle
 * - Screen share toggle
 * - Recording start/stop
 * - Go Live button (from green room)
 * - End session button
 */

import { SessionState } from '../lib/session';

interface ControlsProps {
  isMuted: boolean;
  isVideoOff: boolean;
  isRecording: boolean;
  isScreenSharing: boolean;
  sessionState: SessionState;
  onToggleMute: () => void;
  onToggleVideo: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onGoLive: () => void;
  onScreenShare: () => void;
  onEndSession: () => void;
}

export function Controls({
  isMuted,
  isVideoOff,
  isRecording: _isRecording,
  isScreenSharing,
  sessionState,
  onToggleMute,
  onToggleVideo,
  onStartRecording,
  onStopRecording,
  onGoLive,
  onScreenShare,
  onEndSession
}: ControlsProps) {
  return (
    <div className="controls-bar">
      {/* Left Section - Media Controls */}
      <div className="controls-section left">
        {/* Mute Button */}
        <button
          className={`control-btn ${isMuted ? 'off' : 'on'}`}
          onClick={onToggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <span className="control-icon">{isMuted ? '🔇' : '🎤'}</span>
          <span className="control-label">{isMuted ? 'Unmute' : 'Mute'}</span>
        </button>

        {/* Camera Button */}
        <button
          className={`control-btn ${isVideoOff ? 'off' : 'on'}`}
          onClick={onToggleVideo}
          title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
        >
          <span className="control-icon">{isVideoOff ? '📷' : '🎥'}</span>
          <span className="control-label">{isVideoOff ? 'Start Video' : 'Stop Video'}</span>
        </button>

        {/* Screen Share Button */}
        <button
          className={`control-btn ${isScreenSharing ? 'active' : ''}`}
          onClick={onScreenShare}
          title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
        >
          <span className="control-icon">{isScreenSharing ? '🖥️' : '💻'}</span>
          <span className="control-label">{isScreenSharing ? 'Stop Share' : 'Share Screen'}</span>
        </button>
      </div>

      {/* Center Section - Recording Controls */}
      <div className="controls-section center">
        {sessionState === 'green-room' && (
          <button
            className="control-btn go-live"
            onClick={onGoLive}
            title="Go Live"
          >
            <span className="control-icon">🎬</span>
            <span className="control-label">Go Live</span>
          </button>
        )}

        {sessionState === 'live' && (
          <button
            className="control-btn record-start"
            onClick={onStartRecording}
            title="Start Recording"
          >
            <span className="control-icon">⏺️</span>
            <span className="control-label">Start Recording</span>
          </button>
        )}

        {sessionState === 'recording' && (
          <button
            className="control-btn record-stop recording"
            onClick={onStopRecording}
            title="Stop Recording"
          >
            <span className="rec-pulse"></span>
            <span className="control-icon">⏹️</span>
            <span className="control-label">Stop Recording</span>
          </button>
        )}
      </div>

      {/* Right Section - Session Controls */}
      <div className="controls-section right">
        <button
          className="control-btn end-session"
          onClick={onEndSession}
          title="End Session"
        >
          <span className="control-icon">📴</span>
          <span className="control-label">End</span>
        </button>
      </div>
    </div>
  );
}
